import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api';
import { Search, Filter, Mail, User, BookOpen, Eye, Pencil, Save, X } from 'lucide-react';
import StudentProfileDetail from '../StudentProfileDetail';
import UIElementGuard from '../UIElementGuard';

const avatarColors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4'];

const FacultyStudentDirectory = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('All');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showBatchUpdate, setShowBatchUpdate] = useState(false);
  const [batchAcademicForm, setBatchAcademicForm] = useState({ admission_semester: '', academic_year: '' });
  const [batchUpdateStatus, setBatchUpdateStatus] = useState('');
  const [coursesData, setCoursesData] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  // Edit student profile state
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editStatus, setEditStatus] = useState(''); // '', 'saving', 'saved', or error message

  useEffect(() => { fetchStudents(); }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await api.get('/profile/faculty/students');
      setStudents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStudent?.user_id) fetchStudentCourses(selectedStudent.user_id);
    else setCoursesData([]);
  }, [selectedStudent]);

  const fetchStudentCourses = async (studentId) => {
    try {
      setLoadingCourses(true);
      const res = await api.get(`/faculty/students/${studentId}/courses-and-marks`);
      setCoursesData(res.data);
    } catch (err) { setCoursesData([]); }
    finally { setLoadingCourses(false); }
  };

  const batches = useMemo(() => {
    const batchMap = new Map();
    students.forEach(s => {
      if (!s.academic_batch) return;
      const parts = [];
      if (s.graduation_level) parts.push(s.graduation_level.trim());
      const progDegree = (s.program || s.degree || '').trim();
      if (progDegree && !parts.includes(progDegree)) parts.push(progDegree);
      if (s.branch && s.branch.trim() && !parts.includes(s.branch.trim())) parts.push(s.branch.trim());
      let label = parts.join(' - ');
      label = label ? `${label} (${s.academic_batch})` : s.academic_batch;
      if (!batchMap.has(label)) {
        batchMap.set(label, s.academic_batch);
      }
    });
    const batchList = Array.from(batchMap.entries()).map(([label, value]) => ({ label, value }));
    batchList.sort((a, b) => b.label.localeCompare(a.label));
    return [{ label: 'All', value: 'All' }, ...batchList];
  }, [students]);

  const filteredStudents = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return students.filter(s => {
      const matchSearch = !q
        || (s.full_name || '').toLowerCase().includes(q)
        || (s.usn || '').toLowerCase().includes(q)
        || (s.email || '').toLowerCase().includes(q);
        
      let sLabel = '';
      if (s.academic_batch) {
        const parts = [];
        if (s.graduation_level) parts.push(s.graduation_level.trim());
        const progDegree = (s.program || s.degree || '').trim();
        if (progDegree && !parts.includes(progDegree)) parts.push(progDegree);
        if (s.branch && s.branch.trim() && !parts.includes(s.branch.trim())) parts.push(s.branch.trim());
        sLabel = parts.join(' - ');
        sLabel = sLabel ? `${sLabel} (${s.academic_batch})` : s.academic_batch;
      }
      
      const matchBatch = selectedBatch === 'All' || sLabel === selectedBatch;
      return matchSearch && matchBatch;
    });
  }, [students, searchQuery, selectedBatch]);

  const openBatchUpdate = () => {
    const src = filteredStudents[0];
    setBatchAcademicForm({ admission_semester: src?.admission_semester || '', academic_year: src?.academic_year || '' });
    setBatchUpdateStatus('');
    setShowBatchUpdate(true);
  };

  const openEditStudent = (student) => {
    setEditingStudent(student);
    setEditForm({
      full_name: student.full_name || '',
      phone_number: student.phone_number || '',
      student_date_of_birth: student.student_date_of_birth || '',
      gender: student.gender || '',
      usn: student.usn || '',
      graduation_level: student.graduation_level || '',
      program: student.program || '',
      degree: student.degree || '',
      branch: student.branch || '',
      academic_year: student.academic_year || '',
      academic_batch: student.academic_batch || '',
      admission_semester: student.admission_semester || '',
      admission_number: student.admission_number || '',
      year_of_admission: student.year_of_admission || ''
    });
    setEditStatus('');
  };

  const closeEditStudent = () => {
    setEditingStudent(null);
    setEditForm({});
    setEditStatus('');
  };

  const saveStudentEdits = async () => {
    if (!editingStudent) return;
    if (!editForm.full_name.trim()) {
      setEditStatus('Full name is required.');
      return;
    }
    if (editForm.phone_number && !/^\d{10}$/.test(editForm.phone_number.trim())) {
      setEditStatus('Phone number must contain exactly 10 digits.');
      return;
    }
    setEditStatus('saving');
    try {
      const payload = { ...editForm };
      // Send only non-empty trimmed values
      Object.keys(payload).forEach(k => {
        if (typeof payload[k] === 'string') payload[k] = payload[k].trim();
      });
      await api.put(`/profile/faculty/students/${editingStudent.user_id}`, payload);
      setEditStatus('saved');
      // Refresh the directory list so the table reflects the new values
      const refreshed = await api.get('/profile/faculty/students');
      const list = Array.isArray(refreshed.data) ? refreshed.data : [];
      setStudents(list);
      // Keep the open detail view (if any) in sync with the saved values
      if (editingStudent) {
        setEditingStudent(list.find(s => s.user_id === editingStudent.user_id) || editingStudent);
      }
      if (selectedStudent) {
        setSelectedStudent(list.find(s => s.user_id === selectedStudent.user_id) || selectedStudent);
      }
      setTimeout(() => { closeEditStudent(); }, 700);
    } catch (err) {
      setEditStatus(err.response?.data?.detail || 'Could not update this student. Please try again.');
    }
  };

  const updateBatchAcademicDetails = async () => {
    if (!batchAcademicForm.admission_semester.trim() || !batchAcademicForm.academic_year.trim()) {
      setBatchUpdateStatus('missing'); return;
    }
    setBatchUpdateStatus('saving');
    try {
      await api.put('/profile/faculty/students/batch-academic', {
        batch: selectedBatch,
        admission_semester: batchAcademicForm.admission_semester.trim(),
        academic_year: batchAcademicForm.academic_year.trim(),
        student_ids: filteredStudents.map(s => s.id)
      });
      const refreshed = await api.get('/profile/faculty/students');
      setStudents(Array.isArray(refreshed.data) ? refreshed.data : []);
      setBatchUpdateStatus('saved');
      setTimeout(() => { setShowBatchUpdate(false); setBatchUpdateStatus(''); }, 700);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not update this batch. Please try again.';
      setBatchUpdateStatus(msg);
    }
  };

  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--faculty-muted)' }}>
      Loading student directory...
    </div>
  );

  if (selectedStudent) return (
    <StudentProfileDetail
      student={selectedStudent}
      onBack={() => setSelectedStudent(null)}
      coursesData={coursesData}
      loadingCourses={loadingCourses}
      canEdit
      onEditProfile={openEditStudent}
    />
  );

  return (
    <div className="faculty-student-directory">
      {/* Heading */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '1.6rem', fontWeight: '800', color: 'var(--faculty-text)', margin: 0 }}>
          Students Directory
        </h2>
        <p style={{ color: 'var(--faculty-muted)', marginTop: '6px', fontSize: '0.95rem' }}>
          View and filter all students enrolled in your university.
        </p>
      </div>

      {/* Search + Filter row */}
      <div className="faculty-student-directory-toolbar" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '240px', maxWidth: '380px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--faculty-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search by name, USN, or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 38px',
              border: '1px solid var(--faculty-border)', borderRadius: '8px',
              fontSize: '0.9rem', color: 'var(--faculty-text)', background: '#fff',
              outline: 'none', boxSizing: 'border-box'
            }}
            onFocus={e => e.target.style.borderColor = 'var(--faculty-accent)'}
            onBlur={e => e.target.style.borderColor = 'var(--faculty-border)'}
          />
        </div>

        {/* Filter + Update Semester */}
        <div className="faculty-student-directory-filters" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem', color: 'var(--faculty-muted)', fontWeight: '600', whiteSpace: 'nowrap' }}>
            <Filter size={14} /> Filter by Batch:
          </span>
          <select
            value={selectedBatch}
            onChange={e => setSelectedBatch(e.target.value)}
            style={{ padding: '9px 12px', border: '1px solid var(--faculty-border)', borderRadius: '8px', fontSize: '0.88rem', color: 'var(--faculty-text)', background: '#fff', minWidth: '100px', cursor: 'pointer', outline: 'none' }}
          >
            {batches.map(b => <option key={b.label} value={b.label}>{b.label}</option>)}
          </select>
          <UIElementGuard page="faculty_student_dir" element="batch_update_btn">
            <button
              onClick={openBatchUpdate}
              disabled={filteredStudents.length === 0}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '9px 16px',
                background: 'linear-gradient(135deg, var(--faculty-accent), #2563eb)',
                color: '#fff', border: 'none', borderRadius: '8px',
                fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer',
                whiteSpace: 'nowrap', opacity: filteredStudents.length === 0 ? 0.5 : 1
              }}
            >
              <Save size={14} /> Update Semester
            </button>
          </UIElementGuard>
        </div>
      </div>

      {/* Table */}
      <div className="faculty-student-directory-table-wrap" style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden' }}>
        {filteredStudents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 20px' }}>
            <User size={40} style={{ color: 'var(--faculty-muted)', marginBottom: '12px', display: 'block', margin: '0 auto 12px' }} />
            <p style={{ color: 'var(--faculty-muted)', fontWeight: '600' }}>No students found matching your criteria.</p>
          </div>
        ) : (
          <table className="faculty-student-directory-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                {['STUDENT', 'USN', 'DEPARTMENT', 'BATCH', 'DETAILS'].map(col => (
                  <th key={col} style={{ padding: '12px 20px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student, idx) => {
                const name = student.full_name || 'Unknown';
                const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                const color = avatarColors[name.charCodeAt(0) % avatarColors.length];
                return (
                  <tr
                    key={student.id}
                    style={{ borderBottom: idx < filteredStudents.length - 1 ? '1px solid var(--faculty-border)' : 'none', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Student */}
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '0.85rem', flexShrink: 0 }}>
                          {initials}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--faculty-text)', fontSize: '0.9rem' }}>{name}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--faculty-muted)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                            <Mail size={11} /> {student.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* USN */}
                    <td style={{ padding: '14px 20px', color: 'var(--faculty-text)', fontWeight: '500', fontSize: '0.88rem' }}>
                      {student.usn || <span style={{ color: 'var(--faculty-muted)' }}>—</span>}
                    </td>

                    {/* Department */}
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--faculty-muted)', fontSize: '0.88rem' }}>
                        <BookOpen size={13} />
                        {student.branch || student.department || student.degree || '—'}
                      </div>
                    </td>

                    {/* Batch */}
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{ background: 'var(--faculty-accent-soft)', color: 'var(--faculty-accent)', padding: '3px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
                        {student.academic_batch || '—'}
                      </span>
                    </td>

                    {/* View + Edit */}
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => setSelectedStudent(student)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px', border: '1px solid var(--faculty-border)', borderRadius: '6px', background: '#fff', color: 'var(--faculty-text)', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--faculty-accent)'; e.currentTarget.style.color = 'var(--faculty-accent)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--faculty-border)'; e.currentTarget.style.color = 'var(--faculty-text)'; }}
                        >
                          <Eye size={13} /> View
                        </button>
                        <UIElementGuard page="faculty_student_dir" element="edit_student_btn">
                          <button
                            onClick={() => openEditStudent(student)}
                            title="Edit Student Profile"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', border: '1px solid var(--faculty-border)', borderRadius: '6px', background: '#fff', color: 'var(--faculty-text)', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--faculty-accent)'; e.currentTarget.style.color = 'var(--faculty-accent)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--faculty-border)'; e.currentTarget.style.color = 'var(--faculty-text)'; }}
                          >
                            <Pencil size={13} /> Edit
                          </button>
                        </UIElementGuard>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Update Semester Modal */}
      {showBatchUpdate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(6,19,41,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 24px 80px rgba(0,0,0,0.2)', width: '440px', maxWidth: '100%', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--faculty-border)' }}>
              <div>
                <h3 style={{ margin: 0, fontWeight: '700', color: 'var(--faculty-text)', fontSize: '1.05rem' }}>Update Semester</h3>
                <p style={{ margin: '4px 0 0', color: 'var(--faculty-muted)', fontSize: '0.82rem' }}>
                  Batch: {selectedBatch === 'All' ? 'All visible students' : selectedBatch}
                </p>
              </div>
              <button onClick={() => setShowBatchUpdate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faculty-muted)', padding: '4px', borderRadius: '6px' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.8rem', fontWeight: '700', color: 'var(--faculty-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Semester
                <select
                  value={batchAcademicForm.admission_semester}
                  onChange={e => setBatchAcademicForm(p => ({ ...p, admission_semester: e.target.value }))}
                  style={{ padding: '10px 12px', border: '1px solid var(--faculty-border)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--faculty-text)', background: '#fff', outline: 'none' }}
                >
                  <option value="">Select semester</option>
                  {[1,2,3,4,5,6,7,8].map(s => <option key={s} value={String(s)}>{s}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.8rem', fontWeight: '700', color: 'var(--faculty-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Academic Year (e.g. 2024-25)
                <input
                  type="text"
                  value={batchAcademicForm.academic_year}
                  onChange={e => setBatchAcademicForm(p => ({ ...p, academic_year: e.target.value }))}
                  placeholder="Example: 2026-27"
                  style={{ padding: '10px 12px', border: '1px solid var(--faculty-border)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--faculty-text)', background: '#fff', outline: 'none' }}
                />
              </label>
              {batchUpdateStatus === 'missing' && <p style={{ color: '#dc2626', fontWeight: '600', fontSize: '0.82rem', margin: 0 }}>Semester and academic year are required.</p>}
              {batchUpdateStatus === 'saved' && <p style={{ color: '#16803d', fontWeight: '600', fontSize: '0.82rem', margin: 0 }}>Batch updated successfully.</p>}
              {batchUpdateStatus && !['missing', 'saving', 'saved', ''].includes(batchUpdateStatus) && (
                <p style={{ color: '#dc2626', fontWeight: '600', fontSize: '0.82rem', margin: 0 }}>{batchUpdateStatus}</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
                <button
                  onClick={() => setShowBatchUpdate(false)}
                  style={{ padding: '9px 16px', border: '1px solid var(--faculty-border)', borderRadius: '8px', background: 'transparent', color: 'var(--faculty-text)', fontWeight: '600', fontSize: '0.88rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={updateBatchAcademicDetails}
                  disabled={batchUpdateStatus === 'saving'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', background: 'linear-gradient(135deg, var(--faculty-accent), #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer' }}
                >
                  <Save size={14} /> {batchUpdateStatus === 'saving' ? 'Saving...' : 'Save Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Profile Modal */}
      {editingStudent && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(6,19,41,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 24px 80px rgba(0,0,0,0.2)', width: '520px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--faculty-border)', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
              <div>
                <h3 style={{ margin: 0, fontWeight: '700', color: 'var(--faculty-text)', fontSize: '1.05rem' }}>Edit Student Profile</h3>
                <p style={{ margin: '4px 0 0', color: 'var(--faculty-muted)', fontSize: '0.82rem' }}>
                  {editingStudent.email || ''} — changes are visible to the student and all profile viewers.
                </p>
              </div>
              <button onClick={closeEditStudent} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faculty-muted)', padding: '4px', borderRadius: '6px' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {[
                { key: 'full_name', label: 'Full Name (as per SSLC)', required: true },
                { key: 'phone_number', label: 'Student Mobile Number', maxLength: 10, numeric: true },
                { key: 'student_date_of_birth', label: 'Date of Birth' },
                { key: 'gender', label: 'Gender' },
                { key: 'usn', label: 'USN' },
                { key: 'admission_number', label: 'Admission Number' },
                { key: 'year_of_admission', label: 'Year of Admission' },
                { key: 'graduation_level', label: 'Graduation Level' },
                { key: 'program', label: 'Program' },
                { key: 'degree', label: 'Degree' },
                { key: 'branch', label: 'Branch' },
                { key: 'academic_year', label: 'Academic Year' },
                { key: 'academic_batch', label: 'Academic Batch' },
                { key: 'admission_semester', label: 'Admission Semester' }
              ].map(f => (
                <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  {f.label}{f.required ? ' *' : ''}
                  <input
                    type="text"
                    value={editForm[f.key] || ''}
                    maxLength={f.maxLength}
                    onChange={e => setEditForm(p => ({ ...p, [f.key]: f.numeric ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value }))}
                    style={{
                      padding: '10px 12px', border: `1px solid ${editStatus === 'Full name is required.' && f.key === 'full_name' ? '#dc2626' : 'var(--faculty-border)'}`,
                      borderRadius: '8px', fontSize: '0.9rem', color: 'var(--faculty-text)', background: '#fff', outline: 'none'
                    }}
                  />
                </label>
              ))}
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {editStatus === 'saved' && <p style={{ color: '#16803d', fontWeight: '600', fontSize: '0.82rem', margin: 0 }}>Student profile updated successfully.</p>}
              {editStatus && !['saving', 'saved'].includes(editStatus) && (
                <p style={{ color: '#dc2626', fontWeight: '600', fontSize: '0.82rem', margin: 0 }}>{editStatus}</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={closeEditStudent}
                  style={{ padding: '9px 16px', border: '1px solid var(--faculty-border)', borderRadius: '8px', background: 'transparent', color: 'var(--faculty-text)', fontWeight: '600', fontSize: '0.88rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveStudentEdits}
                  disabled={editStatus === 'saving'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', background: 'linear-gradient(135deg, var(--faculty-accent), #2563eb)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: editStatus === 'saving' ? 0.7 : 1 }}
                >
                  <Save size={14} /> {editStatus === 'saving' ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyStudentDirectory;
